/**
 * Emerald Books — Period Close + Approvals + Rules UI (Phase 11).
 */
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Loader2, Lock, LockOpen, Plus, X, CheckCircle2, XCircle,
  Shield, AlertTriangle, Coins,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { booksClient, errMsg, unwrapList } from './api';

/* ========================================================================= */
/* Period Close                                                                */
/* ========================================================================= */
export const PeriodClosePage = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ period: new Date().toISOString().slice(0, 7), level: 'hard', note: '' });

  const load = async () => {
    setLoading(true);
    try { const r = await booksClient.get('/period-close'); setItems(unwrapList(r)); }
    catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const close = async (e) => {
    e.preventDefault();
    try { await booksClient.post('/period-close', form); toast.success(`Period ${form.period} closed`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reopen = async (period) => {
    if (!window.confirm(`Reopen period ${period}? Requires owner role.`)) return;
    try { await booksClient.post(`/period-close/${period}/reopen`); toast.success('Reopened'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-[#0b1a2e]">Period Close</h1>
        <p className="text-sm text-slate-500">Lock a month · block back-posting · maintain audit trail</p>
      </div>

      <form onSubmit={close} className="bg-white p-4 rounded-lg border grid grid-cols-1 sm:grid-cols-4 gap-2">
        <Input type="month" required value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="period-close-input" />
        <select value={form.level} onChange={(e) => setForm({ ...form, level: e.target.value })} className="px-3 py-2 border rounded text-sm">
          <option value="hard">Hard close (block writes)</option>
          <option value="soft">Soft close (audit only)</option>
        </select>
        <Input placeholder="Note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="sm:col-span-1" />
        <Button type="submit" className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="period-close-submit-btn">
          <Lock className="w-4 h-4 mr-1" /> Close
        </Button>
      </form>

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="grid gap-2 sm:grid-cols-2">
          {items.length === 0 && <div className="col-span-full text-center text-slate-400 py-12">No closed periods yet</div>}
          {items.map((c) => (
            <div key={c.period} className="bg-white rounded-lg border p-3 flex items-center justify-between" data-testid={`period-${c.period}`}>
              <div>
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-rose-600" />
                  <span className="font-semibold text-slate-800">{c.period}</span>
                  <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${c.level === 'hard' ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                    {c.level}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Closed by {c.closed_by_name} · {(c.closed_at || '').slice(0, 10)}</div>
                {c.note && <div className="text-xs text-slate-500 mt-0.5">{c.note}</div>}
              </div>
              <button onClick={() => reopen(c.period)} className="text-xs px-2 py-1 rounded border hover:bg-slate-50 inline-flex items-center gap-1" data-testid={`period-reopen-${c.period}`}>
                <LockOpen className="w-3.5 h-3.5" /> Reopen
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ========================================================================= */
/* Approvals                                                                   */
/* ========================================================================= */
export const ApprovalsPage = () => {
  const [items, setItems] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [showRule, setShowRule] = useState(false);
  const [rule, setRule] = useState({ entity_type: 'journal', min_amount: 0, required_role: 'approver', enabled: true, note: '' });

  const load = async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        booksClient.get('/approvals', { params: filter === 'all' ? {} : { status: filter } }),
        booksClient.get('/approvals/rules'),
      ]);
      setItems(unwrapList(a));
      setRules(unwrapList(r));
    } catch (e) { toast.error(errMsg(e)); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [filter]);

  const saveRule = async (e) => {
    e.preventDefault();
    try {
      await booksClient.post('/approvals/rules', { ...rule, min_amount: Number(rule.min_amount || 0) });
      toast.success('Rule saved');
      setShowRule(false); load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const deleteRule = async (id) => {
    if (!window.confirm('Delete rule?')) return;
    try { await booksClient.delete(`/approvals/rules/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (id) => {
    try { await booksClient.post(`/approvals/${id}/approve`); toast.success('Approved'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const reject = async (id) => {
    const reason = window.prompt('Reason for rejection?', ''); if (reason === null) return;
    try { await booksClient.post(`/approvals/${id}/reject`, { reason }); toast.success('Rejected'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold text-[#0b1a2e]">Approvals</h1>
          <p className="text-sm text-slate-500">Maker-checker · configurable rules per entity type</p>
        </div>
        <Button onClick={() => setShowRule(true)} className="bg-[#0f5c3d] hover:bg-[#0d4d33]" data-testid="approvals-new-rule-btn">
          <Shield className="w-4 h-4 mr-1" /> Rule
        </Button>
      </div>

      {rules.length > 0 && (
        <div className="bg-white p-3 rounded-lg border">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Rules</div>
          <div className="flex flex-wrap gap-2">
            {rules.map((r) => (
              <div key={r.rule_id} className="flex items-center gap-2 bg-slate-50 border rounded px-2 py-1 text-xs">
                <span className="font-medium text-slate-700">{r.entity_type}</span>
                <span className="text-slate-500">≥ {r.min_amount}</span>
                <span className="text-slate-500">→ {r.required_role}</span>
                {!r.enabled && <span className="text-rose-600">disabled</span>}
                <button onClick={() => deleteRule(r.rule_id)}><X className="w-3 h-3 text-slate-400 hover:text-rose-600" /></button>
              </div>
            ))}
          </div>
        </div>
      )}

      {showRule && (
        <form onSubmit={saveRule} className="bg-white p-4 rounded-lg border grid grid-cols-1 sm:grid-cols-4 gap-2">
          <select value={rule.entity_type} onChange={(e) => setRule({ ...rule, entity_type: e.target.value })} className="px-3 py-2 border rounded text-sm">
            <option value="journal">Journal</option>
            <option value="bill">Bill</option>
            <option value="invoice">Invoice</option>
            <option value="payroll_run">Payroll run</option>
            <option value="expense_claim">Expense claim</option>
          </select>
          <Input type="number" step="0.01" placeholder="Min amount" value={rule.min_amount} onChange={(e) => setRule({ ...rule, min_amount: e.target.value })} />
          <select value={rule.required_role} onChange={(e) => setRule({ ...rule, required_role: e.target.value })} className="px-3 py-2 border rounded text-sm">
            <option value="approver">Approver</option>
            <option value="owner">Owner</option>
          </select>
          <Button type="submit" className="bg-[#0f5c3d] hover:bg-[#0d4d33]">Save rule</Button>
        </form>
      )}

      <div className="flex flex-wrap gap-2">
        {['pending', 'approved', 'rejected', 'all'].map((f) => (
          <button key={f} onClick={() => setFilter(f)}
            className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize ${filter === f ? 'bg-[#0b1a2e] text-white' : 'bg-white text-slate-600 border'}`}>
            {f}
          </button>
        ))}
      </div>

      {loading ? <Loader2 className="animate-spin mx-auto text-slate-400" /> : (
        <div className="space-y-2" data-testid="approvals-list">
          {items.length === 0 && <div className="text-center text-slate-400 py-12">No approvals</div>}
          {items.map((a) => (
            <div key={a.approval_id} className="bg-white rounded-lg border p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-mono text-slate-400">{a.approval_id.slice(0, 8)}</span>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ${
                      a.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                      a.status === 'approved' ? 'bg-emerald-50 text-emerald-700' :
                      'bg-rose-50 text-rose-700'
                    }`}>{a.status}</span>
                    <span className="text-xs uppercase tracking-wider bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                      {a.entity_type}
                    </span>
                    <span className="text-xs font-mono text-slate-500">≥ {a.amount?.toFixed?.(2)}</span>
                  </div>
                  <div className="mt-1.5 text-sm text-slate-700">{a.description || '—'}</div>
                  <div className="text-xs text-slate-400 mt-1">Requested by {a.requested_by_name}</div>
                  {a.reject_reason && <div className="text-xs text-rose-600 mt-1">Rejected: {a.reject_reason}</div>}
                </div>
                {a.status === 'pending' && (
                  <div className="flex gap-1">
                    <button onClick={() => approve(a.approval_id)} className="p-2 rounded hover:bg-emerald-50 text-emerald-600" title="Approve" data-testid={`approvals-approve-${a.approval_id}`}>
                      <CheckCircle2 className="w-5 h-5" />
                    </button>
                    <button onClick={() => reject(a.approval_id)} className="p-2 rounded hover:bg-rose-50 text-rose-600" title="Reject">
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
